
import { useState, useEffect } from "react";
import { Bell } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";

interface Profile {
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
}

interface Booking {
  start_time: string;
  end_time: string;
  user_id: string;
  status: string;
  profile?: Profile | null;
}

interface NotificationData {
  id: string;
  message: string;
  created_at: string;
  is_read: boolean;
  booking_id: string;
  owner_id: string;
  booking?: Booking | null;
}

const NotificationBell = () => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState<NotificationData | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchNotifications();
    
    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'booking_notifications'
        },
        async (payload) => {
          console.log('New notification payload:', payload);
          // Fetch the new notification with booking data
          try {
            const { data: notification, error } = await supabase
              .from('booking_notifications')
              .select('*')
              .eq('id', payload.new.id)
              .single();

            if (error) {
              console.error('Error fetching new notification:', error);
              return;
            }

            // Create initial notification object with optional booking property
            const notificationWithBooking: NotificationData = {
              ...notification,
              booking: null
            };

            // Fetch booking data separately
            if (notification.booking_id) {
              const { data: booking, error: bookingError } = await supabase
                .from('bookings')
                .select(`
                  start_time,
                  end_time,
                  user_id,
                  status
                `)
                .eq('id', notification.booking_id)
                .single();

              if (!bookingError && booking) {
                notificationWithBooking.booking = booking;
                
                // If we have a user_id, fetch profile data
                if (booking.user_id) {
                  const { data: profile, error: profileError } = await supabase
                    .from('profiles')
                    .select('first_name, last_name, phone')
                    .eq('id', booking.user_id)
                    .single();
                    
                  if (!profileError && profile) {
                    notificationWithBooking.booking.profile = profile;
                  }
                }
              }
            }

            console.log('New notification with data:', notificationWithBooking);
            setNotifications(prev => [notificationWithBooking, ...prev]);
            setUnreadCount(count => count + 1);
              
            toast({
              title: "New Notification",
              description: notification.message,
            });
          } catch (err) {
            console.error('Error processing new notification:', err);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchNotifications = async () => {
    console.log('Fetching notifications...');
    try {
      // Get basic notification data
      const { data: notificationsData, error } = await supabase
        .from('booking_notifications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching notifications:', error);
        return;
      }

      // Process each notification to get booking and profile data
      const processedNotifications = await Promise.all(
        notificationsData.map(async (notification) => {
          // Create notification object with optional booking property
          const notificationWithBooking: NotificationData = {
            ...notification,
            booking: null
          };

          // If there's a booking_id, fetch booking data
          if (notification.booking_id) {
            try {
              const { data: booking, error: bookingError } = await supabase
                .from('bookings')
                .select(`
                  start_time,
                  end_time,
                  user_id,
                  status
                `)
                .eq('id', notification.booking_id)
                .single();

              if (!bookingError && booking) {
                notificationWithBooking.booking = booking;
                
                // If booking has user_id, fetch profile
                if (booking.user_id) {
                  const { data: profile, error: profileError } = await supabase
                    .from('profiles')
                    .select('first_name, last_name, phone')
                    .eq('id', booking.user_id)
                    .single();
                    
                  if (!profileError && profile) {
                    notificationWithBooking.booking.profile = profile;
                  }
                }
              }
            } catch (err) {
              console.error(`Error fetching booking for notification ${notification.id}:`, err);
            }
          }
          return notificationWithBooking;
        })
      );

      console.log('Processed notifications:', processedNotifications);
      setNotifications(processedNotifications || []);
      setUnreadCount(processedNotifications?.filter(n => !n.is_read).length || 0);
    } catch (err) {
      console.error('Error processing notifications:', err);
    }
  };

  const markAsRead = async (notificationId: string) => {
    const { error } = await supabase
      .from('booking_notifications')
      .update({ is_read: true })
      .eq('id', notificationId);

    if (error) {
      console.error('Error marking notification as read:', error);
      return;
    }

    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, is_read: true } : n
      )
    );
    setUnreadCount(count => Math.max(0, count - 1));
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleNotificationClick = (notification: NotificationData) => {
    console.log('Clicked notification:', notification);
    setSelectedNotification(notification);
    markAsRead(notification.id);
  };

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <button className="relative p-2 hover:bg-primary-700 rounded-full">
            <Bell className="h-6 w-6 text-white" />
            {unreadCount > 0 && (
              <Badge 
                className="absolute -top-1 -right-1 px-2 py-1 bg-red-500"
                variant="destructive"
              >
                {unreadCount}
              </Badge>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 z-50 bg-white">
          <h3 className="font-semibold mb-2 px-2">Notifications</h3>
          <ScrollArea className="h-[300px]">
            {notifications.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No notifications</p>
            ) : (
              <div className="space-y-2">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-2 hover:bg-gray-100 cursor-pointer rounded ${
                      !notification.is_read ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <p className="text-sm">{notification.message}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDate(notification.created_at)}, {formatTime(notification.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>

      <Dialog open={!!selectedNotification} onOpenChange={() => setSelectedNotification(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
          </DialogHeader>
          {selectedNotification?.booking ? (
            <div className="space-y-4">
              <div>
                <h4 className="font-medium">Customer Information</h4>
                <p>Name: {selectedNotification.booking.profile?.first_name || 'N/A'} {selectedNotification.booking.profile?.last_name || ''}</p>
                <p>Phone: {selectedNotification.booking.profile?.phone || 'Not provided'}</p>
              </div>
              <div>
                <h4 className="font-medium">Booking Time</h4>
                <p>Start: {formatDate(selectedNotification.booking.start_time)} {formatTime(selectedNotification.booking.start_time)}</p>
                <p>End: {formatDate(selectedNotification.booking.end_time)} {formatTime(selectedNotification.booking.end_time)}</p>
              </div>
              <div>
                <h4 className="font-medium">Status</h4>
                <p className={`capitalize ${
                  selectedNotification.booking.status === 'confirmed' ? 'text-green-600' :
                  selectedNotification.booking.status === 'failed' ? 'text-red-600' :
                  'text-yellow-600'
                }`}>
                  {selectedNotification.booking.status}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">No booking details available</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationBell;
